<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Logo from '../components/Logo.vue'
import { getLocaleFromPath, addLocaleToPath } from '../i18n/index.js'
import { useLandingRedirect } from '../composables/useLandingRedirect.js'
import useUserStore from '../stores/useUserStore'

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()
const { redirectToFeatures, redirectToPricing, redirectToBlog, redirectToAbout, redirectToContact, redirectToPrivacy, redirectToHome } = useLandingRedirect()

// Get current locale for proper navigation
const currentLocale = computed(() => getLocaleFromPath(router.currentRoute.value.path))

// Footer link configuration - all these are now handled by seo-ssr
const footerLinks = {
  product: [
    { name: 'nav_features', action: redirectToFeatures, isExternal: true },
    { name: 'nav_blog', action: redirectToBlog, isExternal: true }
  ],
  legal: [
    { name: 'footer_privacy', action: redirectToPrivacy, isExternal: true }
  ]
}

// Social links - aligned with geo-ssr
const socialLinks = [
  { 
    name: 'Twitter', 
    url: 'https://x.com/idou_ai',
    ariaLabel: 'Follow us on X (Twitter)'
  },
  { 
    name: 'LinkedIn', 
    url: 'https://www.linkedin.com/company/idouai',
    ariaLabel: 'Follow us on LinkedIn'
  }
]

// Handle link clicks - redirect to seo-ssr for migrated pages or stay in seo-ui for auth
const handleLinkClick = (link) => {
  if (link.isExternal) {
    link.action() // Call the redirect function
  } else {
    // Local navigation for seo-ui pages
    const localizedPath = addLocaleToPath(link.path, currentLocale.value)
    router.push(localizedPath)
  }
}

// Handle logo click - redirect to seo-ssr home if logged out, or to app if logged in
const handleLogoClick = () => {
  if (userStore.isAuthenticated) {
    // Let router guard handle navigation based on subscription status
    const homePath = addLocaleToPath('/', currentLocale.value)
    router.push(homePath)
  } else {
    // Redirect to seo-ssr home for unauthenticated users
    redirectToHome()
  }
}

const currentYear = new Date().getFullYear();
</script>

<template>
  <footer class="footer">
    <!-- Decorative gradient orbs -->
    <div class="footer__orb footer__orb--top"></div>
    <div class="footer__orb footer__orb--bottom"></div>
    
    <div class="footer__container">
      <!-- Main Footer Content -->
      <div class="footer-main-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
        <!-- Logo and Description -->
        <div class="lg:col-span-1">
          <a 
            href="/" 
            class="brand-link"
            @click.prevent="handleLogoClick"
          >
            <Logo size="sm" variant="white" />
          </a>
          <p class="footer-description">
            {{ $t('footer_description') }}
          </p>
        </div>

        <!-- Product Links -->
        <div>
          <h3 class="footer-section-title">{{ $t('footer_product') }}</h3>
          <ul>
            <li v-for="link in footerLinks.product" :key="link.name">
              <a
                href="#"
                class="footer-link"
                @click.prevent="handleLinkClick(link)"
              >
                {{ $t(link.name) }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Legal Links -->
        <div>
          <h3 class="footer-section-title">{{ $t('footer_legal') }}</h3>
          <ul>
            <li v-for="link in footerLinks.legal" :key="link.name">
              <a
                href="#"
                class="footer-link"
                @click.prevent="handleLinkClick(link)"
              >
                {{ $t(link.name) }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="footer-bottom">
        <p class="footer-copyright">
          © {{ currentYear }} {{ t('footer_copyright') }}
        </p>
        
        <!-- Social Links - same position as geo-ssr -->
        <div class="footer-social">
          <a
            v-for="social in socialLinks"
            :key="social.name"
            :href="social.url"
            target="_blank"
            rel="noopener noreferrer"
            class="footer-social-link"
            :aria-label="social.ariaLabel"
          >
            <!-- Twitter/X Icon -->
            <svg v-if="social.name === 'Twitter'" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            
            <!-- LinkedIn Icon -->
            <svg v-else-if="social.name === 'LinkedIn'" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
/* ========================================
   FOOTER BASE STYLES - Dark theme aligned with geo-ssr
   ======================================== */
.footer {
  position: relative;
  background: #0f172a;
  color: #cbd5e1;
  overflow: hidden;
}

/* ========================================
   DECORATIVE GRADIENT ORBS
   ======================================== */
.footer__orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
}

.footer__orb--top {
  top: -200px;
  right: -100px;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
}

.footer__orb--bottom {
  bottom: -100px;
  left: -100px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%);
}

/* ========================================
   FOOTER CONTAINER - Aligned with content containers
   ======================================== */
.footer__container {
  position: relative;
  z-index: 1;
  max-width: var(--container-max-width-default);
  margin: 0 auto;
  padding: 80px var(--container-padding-mobile) 40px;
}

@media (min-width: 640px) {
  .footer__container {
    padding: 80px var(--container-padding-tablet) 40px;
  }
}

@media (min-width: 1024px) {
  .footer__container {
    padding: 80px var(--container-padding-desktop) 40px;
  }
}

@media (min-width: 1280px) {
  .footer__container {
    padding: 80px var(--container-padding-large) 40px;
  }
}

/* ========================================
   FOOTER GRID
   ======================================== */
.footer-main-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  margin-bottom: 64px;
}

@media (min-width: 640px) {
  .footer-main-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }
}

@media (min-width: 1024px) {
  .footer-main-grid {
    grid-template-columns: 2fr 1fr 1fr;
    gap: 64px;
    align-items: start;
  }
}

/* ========================================
   BRAND COLUMN
   ======================================== */
.brand-link {
  display: inline-flex;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.brand-link:hover {
  opacity: 0.8;
}

.footer__container p {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: #94a3b8;
  max-width: 320px;
  margin: 1rem 0 0 0;
}

.footer-description {
  margin-top: 1rem;
}

/* ========================================
   FOOTER SECTION TITLES
   ======================================== */
.footer-section-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin: 0 0 20px 0;
}

/* ========================================
   FOOTER LINKS
   ======================================== */
.footer__container ul {
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.footer__container .footer-link {
  display: inline-block;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #cbd5e1;
  text-decoration: none;
  transition: all 0.2s ease;
  position: relative;
}

.footer__container .footer-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background: #3b82f6;
  transition: width 0.2s ease;
}

.footer__container .footer-link:hover {
  color: #ffffff;
  transform: translateX(4px);
}

.footer__container .footer-link:hover::after {
  width: 100%;
}

/* Bottom section links */
.footer__container .mt-12 a {
  font-size: 13px;
}

/* ========================================
   BOTTOM BAR
   ======================================== */
.footer-bottom {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

@media (min-width: 640px) {
  .footer-bottom {
    flex-direction: row;
    justify-content: space-between;
  }
}

.footer-copyright {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin: 0;
  text-align: center;
}

@media (min-width: 640px) {
  .footer-copyright {
    text-align: left;
  }
}

/* ========================================
   SOCIAL LINKS - positioned in bottom bar like geo-ssr
   ======================================== */
.footer-social {
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  color: #94a3b8;
  text-decoration: none;
  transition: all 0.2s ease;
}

.footer-social-link:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  transform: translateY(-2px);
}

.footer-social-link:active {
  transform: translateY(0);
}

/* ========================================
   MOBILE RESPONSIVE
   ======================================== */
@media (max-width: 639px) {
  .footer-main-grid {
    text-align: center;
  }
  
  .footer-main-grid > div:first-child {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .footer__container p {
    max-width: 100%;
  }
  
  .footer__container .footer-link:hover {
    transform: translateX(0);
  }
  
  .footer__container .footer-link::after {
    left: 50%;
    transform: translateX(-50%);
  }
  
  .footer-social {
    justify-content: center;
  }
  
  .footer-bottom {
    text-align: center;
  }
  
  .footer-legal-links {
    justify-content: center;
  }
}

/* ========================================
   ACCESSIBILITY
   ======================================== */
@media (prefers-reduced-motion: reduce) {
  .footer__container .footer-link,
  .footer-social-link,
  .brand-link {
    transition: none;
  }
  
  .footer__orb {
    display: none;
  }
}

/* Focus states */
.footer__container .footer-link:focus-visible,
.footer-social-link:focus-visible,
.brand-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .footer {
    background: #000000;
  }
  
  .footer__container .footer-link {
    color: #ffffff;
  }
}
</style>
